/**
 * ⚡ IndexedDB Cache Manager for HalaRobo Dashboard
 *
 * Provides persistent client-side caching with:
 * - IndexedDB storage (no 5MB limit like sessionStorage)
 * - TTL-based expiration
 * - Version-aware invalidation (checks /api/data/version before re-fetching)
 * - Stale-while-revalidate pattern (show cached data instantly, refresh in background)
 */

const DB_NAME = 'HalaRoboCache';
const DB_VERSION = 1;
const STORE_NAME = 'apiCache';
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

// In-flight request deduplication map
// Maps endpoint -> Promise to prevent duplicate network requests
const inFlightRequests = new Map<string, Promise<any>>();

interface CacheEntry {
  key: string;
  data: any;
  timestamp: number;
  dataHash: string; // Server data version hash
  ttl: number;
}

// ============================================================
// IndexedDB Setup
// ============================================================
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ============================================================
// Cache Operations
// ============================================================

/**
 * Get cached data from IndexedDB
 */
export async function getCached(key: string): Promise<CacheEntry | null> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => {
        const entry = request.result as CacheEntry | undefined;
        if (!entry) return resolve(null);

        // Check if expired
        const age = Date.now() - entry.timestamp;
        if (age > entry.ttl) {
          // Expired but still return it for stale-while-revalidate
          resolve({ ...entry, _expired: true } as any);
        } else {
          resolve(entry);
        }
      };
      request.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

/**
 * Store data in IndexedDB cache
 */
export async function setCache(key: string, data: any, dataHash: string, ttl: number = DEFAULT_TTL): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put({
        key,
        data,
        timestamp: Date.now(),
        dataHash,
        ttl
      } as CacheEntry);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve(); // Don't fail if cache write fails
    });
  } catch {
    // Silently fail - cache is optional
  }
}

/**
 * Clear all cached data
 */
export async function clearCache(): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    // Silently fail
  }
}

/**
 * Clear all cache entries whose key starts with the given prefix
 * Example: clearCacheByPrefix('/expiry') clears all keys starting with '/expiry'
 */
export async function clearCacheByPrefix(prefix: string): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAllKeys();

      request.onsuccess = () => {
        const keys = request.result as string[];
        const keysToDelete = keys.filter(key => key.startsWith(prefix));

        keysToDelete.forEach(key => {
          store.delete(key);
        });

        tx.oncomplete = () => resolve();
      };
      request.onerror = () => resolve();
    });
  } catch {
    // Silently fail
  }
}

// ============================================================
// Smart Fetch with Cache
// ============================================================

interface FetchOptions {
  ttl?: number;          // Cache TTL in ms (default: 5 min)
  forceRefresh?: boolean; // Skip cache and fetch fresh
  timeout?: number;       // Axios timeout
}

/**
 * Check server data version (tiny ~100 byte request)
 * Returns the current data hash, or null if unavailable
 */
export async function checkServerVersion(apiBase: string): Promise<{ hash: string; lastUpdated: string; loaded: boolean } | null> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

    const response = await fetch(`${apiBase}/data/version`, {
      signal: controller.signal,
      headers: { 'Cache-Control': 'no-cache' }
    });
    clearTimeout(timeoutId);

    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Smart cached fetch - the main function to use
 *
 * Flow:
 * 1. Check IndexedDB for cached data
 * 2. If cached & not expired → return immediately (no network!)
 * 3. If cached but expired → return cached data AND refresh in background
 * 4. If no cache → fetch from server, cache the result
 * 5. Uses /api/data/version to avoid re-downloading unchanged data
 */
export async function smartFetch(
  apiBase: string,
  endpoint: string,
  options: FetchOptions = {}
): Promise<{ data: any; fromCache: boolean; refreshing: boolean }> {
  const cacheKey = endpoint;
  const { ttl = DEFAULT_TTL, forceRefresh = false, timeout = 600000 } = options;

  // Step 1: Check cache
  if (!forceRefresh) {
    const cached = await getCached(cacheKey);

    if (cached) {
      const isExpired = (cached as any)._expired;

      if (!isExpired) {
        // Fresh cache - return immediately, no network needed!
        console.log(`⚡ Cache HIT (fresh): ${endpoint}`);
        return { data: cached.data, fromCache: true, refreshing: false };
      }

      // Expired cache - check if server data has changed
      const serverVersion = await checkServerVersion(apiBase);

      if (serverVersion && serverVersion.hash === cached.dataHash) {
        // Server data hasn't changed! Refresh the cache timestamp and return cached data
        console.log(`⚡ Cache VALID (server unchanged): ${endpoint}`);
        await setCache(cacheKey, cached.data, cached.dataHash, ttl);
        return { data: cached.data, fromCache: true, refreshing: false };
      }

      // Server data changed - return stale data now, refresh in background
      console.log(`🔄 Cache STALE (refreshing): ${endpoint}`);

      // Fire background refresh (non-blocking)
      fetchAndCache(apiBase, endpoint, ttl, timeout).catch(() => {});

      return { data: cached.data, fromCache: true, refreshing: true };
    }
  }

  // Step 2: No cache or force refresh - fetch from server
  console.log(`🌐 Cache MISS: ${endpoint}`);
  const data = await fetchAndCache(apiBase, endpoint, ttl, timeout);
  return { data, fromCache: false, refreshing: false };
}

/**
 * Fetch from server and update cache
 * Uses in-flight deduplication to prevent duplicate requests
 */
async function fetchAndCache(apiBase: string, endpoint: string, ttl: number, timeout: number): Promise<any> {
  // Check if this endpoint is already being fetched
  if (inFlightRequests.has(endpoint)) {
    console.log(`⏳ Reusing in-flight request: ${endpoint}`);
    return inFlightRequests.get(endpoint)!;
  }

  // Create the fetch promise
  const fetchPromise = (async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(`${apiBase}${endpoint}`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();

      // Get current server version for cache tagging
      const serverVersion = await checkServerVersion(apiBase);
      const hash = serverVersion?.hash || Date.now().toString();

      // Store in cache
      await setCache(endpoint, data, hash, ttl);

      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    } finally {
      // Remove from in-flight map when done
      inFlightRequests.delete(endpoint);
    }
  })();

  // Store in-flight request
  inFlightRequests.set(endpoint, fetchPromise);

  return fetchPromise;
}

// ============================================================
// Simpler Cached Fetch (for component-level caching)
// ============================================================

/**
 * Simple cached fetch for components that want basic caching
 * without the full smartFetch + version checking flow
 *
 * - Uses IndexedDB cache with URL as key
 * - If cached and fresh, returns from cache
 * - If expired or missing, fetches from network and caches result
 * - Uses in-flight deduplication
 * - Does NOT check /api/data/version endpoint
 */
export async function cachedFetch(
  url: string,
  options?: { ttl?: number; forceRefresh?: boolean; timeout?: number }
): Promise<any> {
  const cacheKey = url;
  const { ttl = DEFAULT_TTL, forceRefresh = false, timeout = 600000 } = options || {};

  // Step 1: Check cache unless force refresh
  if (!forceRefresh) {
    const cached = await getCached(cacheKey);

    if (cached) {
      const isExpired = (cached as any)._expired;

      if (!isExpired) {
        // Fresh cache - return immediately
        console.log(`⚡ cachedFetch HIT (fresh): ${url}`);
        return cached.data;
      }
    }
  }

  // Step 2: Fetch from network (with deduplication)
  console.log(`🌐 cachedFetch MISS: ${url}`);

  // Check if this URL is already being fetched
  if (inFlightRequests.has(cacheKey)) {
    console.log(`⏳ Reusing in-flight request: ${url}`);
    return inFlightRequests.get(cacheKey)!;
  }

  // Create the fetch promise
  const fetchPromise = (async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();

      // Cache the result (use timestamp as dummy hash since we don't check versions)
      await setCache(cacheKey, data, Date.now().toString(), ttl);

      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    } finally {
      // Remove from in-flight map when done
      inFlightRequests.delete(cacheKey);
    }
  })();

  // Store in-flight request
  inFlightRequests.set(cacheKey, fetchPromise);

  return fetchPromise;
}

// ============================================================
// Cache Statistics (for debugging)
// ============================================================
export async function getCacheStats(): Promise<{ entries: number; keys: string[] }> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAllKeys();

      request.onsuccess = () => {
        resolve({
          entries: request.result.length,
          keys: request.result as string[]
        });
      };
      request.onerror = () => resolve({ entries: 0, keys: [] });
    });
  } catch {
    return { entries: 0, keys: [] };
  }
}
